import { UNIT_LABEL, getSettings, toDisplayDistance } from './settings.mjs';

/*
 * Distances always arrive in kilometres, because that is what the geometry and
 * the scoring work in; the unit is a display choice applied here at the last
 * moment. Defaulting the parameter keeps every existing caller honest and means
 * a missed one shows kilometres rather than a wrong number.
 */
export function formatDistance(km, units = 'km') {
  const value = toDisplayDistance(km, units);
  const label = UNIT_LABEL[units] || UNIT_LABEL.km;
  if (value < 1) return `less than 1 ${label}`;
  if (value < 10) return `${value.toFixed(1)} ${label}`;
  return `${Math.round(value).toLocaleString()} ${label}`;
}

/*
 * Both clock faces, from one place. The setting defaults out of the store rather
 * than being threaded through eleven call sites, four of which are inside the
 * tutorial's pure caption functions where there is no component to hold a hook.
 * Passing it explicitly still works, which is what the tests do.
 */
function clockFace(hours, minutes, clock) {
  const mm = String(minutes).padStart(2, '0');
  if (clock === '24') return `${String(hours).padStart(2, '0')}:${mm}`;
  const suffix = hours < 12 ? 'AM' : 'PM';
  return `${hours % 12 === 0 ? 12 : hours % 12}:${mm} ${suffix}`;
}

export function formatUtcClock(date, clock = getSettings().clock) {
  return clockFace(date.getUTCHours(), date.getUTCMinutes(), clock);
}

export function formatDate(date) {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

/*
 * Month and day without the year, for On This Night rounds before the guess is
 * in. The day is not a secret — it is today's — but the year would name the
 * event, and it is worth about 1.4 degrees of sky per century, so withholding it
 * costs the navigation nothing.
 */
export function formatMonthDay(date) {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function formatCoords(lat, lon) {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lon).toFixed(1)}° ${ew}`;
}

export function moonLabel(moon) {
  const percent = Math.round(moon.illuminated * 100);
  if (percent <= 2) return 'New moon';
  if (percent >= 98) return 'Full moon';
  const phase = percent < 50 ? 'crescent' : 'gibbous';
  return `${moon.waxing ? 'Waxing' : 'Waning'} ${phase}, ${percent}% lit`;
}

/*
 * Local solar time at a longitude, which is what the sky actually shows: the Sun
 * crosses your meridian at noon regardless of what any timezone says. Fifteen
 * degrees is an hour.
 *
 * Shown live as the pin moves because it is the working end of the longitude
 * method. Rounds are always set during astronomical night, so a pin implying
 * three in the afternoon is on the wrong side of the planet however good its
 * latitude looks.
 */
export function localSolarTime(date, lon, clock = getSettings().clock) {
  const decimal = (date.getUTCHours() + date.getUTCMinutes() / 60 + lon / 15 + 24) % 24;
  let hours = Math.floor(decimal);
  let minutes = Math.round((decimal - hours) * 60);
  if (minutes === 60) {
    minutes = 0;
    hours = (hours + 1) % 24;
  }
  return clockFace(hours, minutes, clock);
}
